/**
 * Layout wrapper for Asset module routes.
 * Connects the asset WebSocket to receive real-time operation results
 * (submit, approve, reject, version) and shows toast notifications.
 */

import { Outlet } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useAssetWebSocket } from '../hooks/useAssetWebSocket'
import { AssetOperationEventData } from '../types/websocket'

const AssetLayout = () => {
    const { t } = useTranslation('asset')
    const queryClient = useQueryClient()

    const invalidateAssetQueries = () => {
        const keys = [
            QUERY_KEYS.ASSETS,
            QUERY_KEYS.ASSETS_DRAFT,
            QUERY_KEYS.ASSET_DETAIL,
            'asset-versions',
        ]
        keys.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: [key] })
        })
    }

    const handleCompleted = (data: AssetOperationEventData) => {
        const { code, message } = data
        toast.success(t('ws_success.' + code, { message }))

        invalidateAssetQueries()
    }

    const handleFailed = (data: AssetOperationEventData) => {
        const { code, error } = data
        toast.error(t('ws_errors.' + code, { error }))
        invalidateAssetQueries()
    }

    useAssetWebSocket({
        onCompleted: handleCompleted,
        onFailed: handleFailed,
    })

    return <Outlet />
}

export default AssetLayout
